import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";

import { useTooltipAutoClose } from "../../../lib/useTooltipAutoClose";
import classes from "./WorkQDeferredTooltip.module.css";

const loadBodySimpleModalV2 = () => import("../../common/modal/BodySimpleModalV2");
const loadBodySimpleModalV3 = () => import("../../common/modal/BodySimpleModalV3");
const LazyBodySimpleModalV2 = lazy(loadBodySimpleModalV2);
const LazyBodySimpleModalV3 = lazy(loadBodySimpleModalV3);
let isBodySimpleModalV2Preloaded = false;
let isBodySimpleModalV3Preloaded = false;
let tooltipIdCounter = 0;
const WORKQ_TOOLTIP_OPEN_EVENT = "workq-deferred-tooltip-open";

function pointerMovedIntoOverlay(event) {
    const overlay = document.getElementById("overlay-root");
    if (!overlay || !event?.relatedTarget) {
        return false;
    }
    const related = event.relatedTarget;
    return typeof related.closest === "function" ? overlay.contains(related) : overlay === related;
}

export default function WorkQDeferredTooltip({ mode = "v2", children, wrapperClassName, fillTrigger = true, ...modalProps }) {
    const [isTooltipReady, setIsTooltipReady] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [keepModalMounted, setKeepModalMounted] = useState(false);
    const containerRef = useRef(null);
    const tooltipIdRef = useRef(0);
    const hoverStateRef = useRef(false);
    const leaveRafRef = useRef(null);

    if (!tooltipIdRef.current) {
        tooltipIdCounter += 1;
        tooltipIdRef.current = tooltipIdCounter;
    }

    const closeHover = useCallback(() => {
        hoverStateRef.current = false;
        setIsHovering(false);
    }, []);

    useEffect(() => {
        hoverStateRef.current = isHovering;
    }, [isHovering]);

    useEffect(() => {
        if (isTooltipReady && isHovering) {
            setKeepModalMounted(true);
        }
    }, [isTooltipReady, isHovering]);

    useEffect(() => {
        return () => {
            if (leaveRafRef.current != null) {
                cancelAnimationFrame(leaveRafRef.current);
            }
        };
    }, []);

    const getPortalTooltipEl = useCallback(() => {
        const overlay = document.getElementById("overlay-root");
        return overlay?.querySelector(`[data-tooltip-broadcast-id="${tooltipIdRef.current}"]`) ?? null;
    }, []);

    useTooltipAutoClose({
        isOpen: isHovering,
        onClose: closeHover,
        triggerRef: containerRef,
        tooltipRef: getPortalTooltipEl,
        interactive: isTooltipReady,
        enableHoverPoll: !isTooltipReady,
    });

    useEffect(() => {
        if (!isTooltipReady || !isHovering || !containerRef.current) {
            return;
        }
        const rafId = window.requestAnimationFrame(() => {
            containerRef.current?.dispatchEvent(
                new PointerEvent("pointerover", { bubbles: true, cancelable: true }),
            );
        });
        return () => window.cancelAnimationFrame(rafId);
    }, [isTooltipReady, isHovering]);

    useEffect(() => {
        const onTooltipOpened = (event) => {
            if (event?.detail?.tooltipId === tooltipIdRef.current) {
                return;
            }
            closeHover();
        };
        window.addEventListener(WORKQ_TOOLTIP_OPEN_EVENT, onTooltipOpened);
        return () => window.removeEventListener(WORKQ_TOOLTIP_OPEN_EVENT, onTooltipOpened);
    }, [closeHover]);

    const onPointerEnter = () => {
        if (leaveRafRef.current != null) {
            cancelAnimationFrame(leaveRafRef.current);
            leaveRafRef.current = null;
        }
        window.dispatchEvent(
            new CustomEvent(WORKQ_TOOLTIP_OPEN_EVENT, {
                detail: { tooltipId: tooltipIdRef.current },
            }),
        );
        hoverStateRef.current = true;
        setIsHovering(true);
        if (!isTooltipReady) {
            const isPreloaded = mode === "v3" ? isBodySimpleModalV3Preloaded : isBodySimpleModalV2Preloaded;
            if (isPreloaded) {
                setIsTooltipReady(true);
                return;
            }
            const loader = mode === "v3" ? loadBodySimpleModalV3 : loadBodySimpleModalV2;
            loader()
                .then(() => {
                    if (mode === "v3") {
                        isBodySimpleModalV3Preloaded = true;
                    } else {
                        isBodySimpleModalV2Preloaded = true;
                    }
                    if (!hoverStateRef.current) {
                        return;
                    }
                    setIsTooltipReady(true);
                })
                .catch(() => {});
        }
    };

    const onPointerLeave = (event) => {
        if (pointerMovedIntoOverlay(event)) {
            return;
        }
        if (!isTooltipReady) {
            closeHover();
            return;
        }
        if (leaveRafRef.current != null) {
            cancelAnimationFrame(leaveRafRef.current);
        }
        leaveRafRef.current = requestAnimationFrame(() => {
            leaveRafRef.current = null;
            if (!hoverStateRef.current) {
                return;
            }
            if (containerRef.current?.matches(":hover")) {
                return;
            }
            const tip = getPortalTooltipEl();
            if (tip?.matches(":hover")) {
                return;
            }
            closeHover();
        });
    };

    const content = (
        <div
            ref={containerRef}
            className={`${classes.wrapper} ${wrapperClassName ? wrapperClassName : ""}`}
            onPointerEnter={onPointerEnter}
            onPointerLeave={onPointerLeave}
        >
            {children}
            {isHovering && !isTooltipReady && (
                <span className={classes.hover_loading} aria-label="Loading tooltip">
                    <span className="loader dark" />
                </span>
            )}
        </div>
    );

    if (!keepModalMounted) {
        return content;
    }

    if (mode === "v3") {
        return (
            <Suspense fallback={content}>
                <LazyBodySimpleModalV3 {...modalProps} tooltipBroadcastId={tooltipIdRef.current} openOnMount={isHovering} fillTrigger={fillTrigger}>
                    {content}
                </LazyBodySimpleModalV3>
            </Suspense>
        );
    }

    return (
        <Suspense fallback={content}>
            <LazyBodySimpleModalV2 {...modalProps} tooltipBroadcastId={tooltipIdRef.current} openOnMount={isHovering}>
                {content}
            </LazyBodySimpleModalV2>
        </Suspense>
    );
}
